<template>
    <div class="xtxx">
        <div class="content">

            <div class="invoice-card" v-if="invoice">
                <!-- 发票类型头部 -->
                <div class="invoice-type-header">
                    <div class="type-icon">
                        <svg viewBox="0 0 24 24" width="30" height="30">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="#ff6b35" stroke-width="2"/>
                            <polyline points="14 2 14 8 20 8" fill="none" stroke="#ff6b35" stroke-width="2"/>
                            <line x1="8" y1="13" x2="16" y2="13" stroke="#ff6b35" stroke-width="2"/>
                            <line x1="8" y1="17" x2="16" y2="17" stroke="#ff6b35" stroke-width="2"/>
                        </svg>
                    </div>
                    <span class="type-text">{{ fpzlText }}</span>
                    <div class="status-tag" :class="statusClass">{{ statusText }}</div>
                </div>

                <!-- 开票信息 -->
                <div class="info-section">
                    <div class="info-row" v-if="invoice.invoiceKprq">
                        <span class="info-label">开票日期</span>
                        <span class="info-value">{{ invoice.invoiceKprq }}</span>
                    </div>
                    <div class="info-row" v-if="invoice.fpdm">
                        <span class="info-label">发票代码</span>
                        <span class="info-value">{{ invoice.fpdm }}</span>
                    </div>
                    <div class="info-row" v-if="invoice.fphm">
                        <span class="info-label">发票号码</span>
                        <span class="info-value">{{ invoice.fphm }}</span>
                    </div>
                    <div class="info-row" v-if="invoice.jym">
                        <span class="info-label">校验码</span>
                        <span class="info-value">{{ invoice.jym }}</span>
                    </div>
                </div>

                <div class="preview-divider"></div>

                <!-- 购买方信息 -->
                <div class="info-section">
                    <div class="info-row">
                        <span class="info-label">购买方名称</span>
                        <span class="info-value">{{ invoice.gfmc || '-' }}</span>
                    </div>
                    <div class="info-row" v-if="invoice.gfsh">
                        <span class="info-label">购买方税号</span>
                        <span class="info-value">{{ invoice.gfsh }}</span>
                    </div>
                </div>

                <div class="preview-divider"></div>

                <!-- 销售方信息 -->
                <div class="info-section">
                    <div class="info-row">
                        <span class="info-label">销售方名称</span>
                        <span class="info-value">{{ invoice.xfmc || '-' }}</span>
                    </div>
                    <div class="info-row" v-if="invoice.xfsh">
                        <span class="info-label">销售方税号</span>
                        <span class="info-value">{{ invoice.xfsh }}</span>
                    </div>
                </div>

                <div class="preview-divider"></div>

                <!-- 项目明细 -->
                <div class="info-section" v-if="invoice.items && invoice.items.length > 0">
                    <div class="item-header-row">
                        <span>项目名称</span>
                        <span>金额</span>
                    </div>
                    <div class="item-row" v-for="(item, idx) in invoice.items" :key="idx">
                        <div class="item-name">{{ item.spmc }}</div>
                        <div class="item-amount">¥{{ formatAmount(item.je) }}</div>
                    </div>
                </div>

                <!-- 合计金额 -->
                <div class="total-section">
                    <span class="total-label">合计金额</span>
                    <span class="total-amount">¥{{ formatAmount(invoice.jshj) }}</span>
                </div>

                <!-- 备注 -->
                <div class="remark-row" v-if="invoice.bz">
                    <span class="remark-label">备注</span>
                    <span class="remark-value">{{ invoice.bz }}</span>
                </div>

                <!-- 发票状态说明 -->
                <div class="status-desc" v-if="invoice.orderStatus === '1'">
                    发票正在开具中，请稍后查看
                </div>
                <div class="status-desc failed" v-if="invoice.orderStatus === '3'">
                    发票开具失败，请重新申请
                </div>
                <div class="status-desc" v-if="invoice.orderStatus === '2' && invoice.fpzt === '10'">
                    红冲申请处理中，请稍后查看
                </div>

                <!-- 操作按钮（仅已开票时显示） -->
                <div class="action-section" v-if="invoice.orderStatus === '2'">
                    <!-- 从开票记录进入：只显示下载发票 -->
                    <template v-if="isFromHistory">
                        <button class="action-btn secondary" @click="downloadInvoice">下载发票</button>
                    </template>
                    <!-- 从缴费详情进入 -->
                    <template v-else>
                        <!-- 红冲发票：开票 + 下载发票 -->
                        <template v-if="isRedInvoice">
                            <button class="action-btn primary" v-if="invoice.invoiceEnabled" @click="reInvoice">开票</button>
                            <button class="action-btn secondary" @click="downloadInvoice">下载发票</button>
                        </template>
                        <!-- 正常发票 -->
                        <template v-else>
                            <button class="action-btn secondary" @click="downloadInvoice">下载发票</button>
                            <button class="action-btn red" v-if="!invoice.fpzt || invoice.fpzt === '0'" @click="showRedModal = true">
                                发起红冲
                            </button>
                            <!-- 红冲后重新开票 -->
                            <button class="action-btn primary" v-if="invoice.fpzt === '1' && invoice.invoiceEnabled" @click="reInvoice">
                                重新开票
                            </button>
                        </template>
                    </template>
                </div>
            </div>

            <!-- 红冲弹窗 -->
            <van-popup v-model="showRedModal" position="center" round :style="{ width: '90%', maxWidth: '360px' }">
                <div class="red-modal">
                    <div class="red-header">
                        <h3 class="red-title">发起红冲</h3>
                        <van-icon name="cross" class="red-close" @click="showRedModal = false" />
                    </div>
                    <div class="red-body">
                        <div class="red-form-item">
                            <label class="red-form-label">红冲原因</label>
                            <van-field
                                v-model="redReasonText"
                                readonly
                                clickable
                                placeholder="请选择红冲原因"
                                right-icon="arrow-down"
                                @click="showReasonPicker = true"
                                class="red-form-field"
                            />
                        </div>
                        <div class="red-form-item">
                            <label class="red-form-label">备注</label>
                            <van-field
                                v-model="redRemark"
                                type="textarea"
                                placeholder="请输入备注信息（选填）"
                                rows="3"
                                class="red-form-field"
                            />
                        </div>
                        <div class="red-warning">
                            <van-icon name="warning-o" />
                            <span>红冲后将生成红字发票，原发票将失效</span>
                        </div>
                    </div>
                    <div class="red-footer">
                        <button class="red-btn cancel" @click="showRedModal = false">取消</button>
                        <button class="red-btn confirm" @click="confirmRed">确认红冲</button>
                    </div>
                </div>
            </van-popup>

            <!-- 红冲原因选择器 -->
            <van-popup v-model="showReasonPicker" position="bottom" round>
                <van-picker
                    :columns="reasonColumns"
                    @confirm="onReasonConfirm"
                    @cancel="showReasonPicker = false"
                    show-toolbar
                    title="选择红冲原因"
                />
            </van-popup>

            <!-- 发送邮箱弹窗 -->
            <van-dialog
                v-model="showEmailModal"
                title="发送发票到邮箱"
                show-cancel-button
                @confirm="confirmSendEmail"
            >
                <div style="padding: 16px;">
                    <van-field v-model="emailAddr" placeholder="请输入邮箱地址" type="email" />
                </div>
            </van-dialog>

            <!-- 未找到发票 -->
            <div class="empty-state" v-if="loaded && !invoice">
                <span>未找到发票信息</span>
            </div>

            <!-- 加载中 -->
            <div class="loading-state" v-if="!loaded">
                <van-loading type="spinner" color="#00b5ff">加载中...</van-loading>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            invoice: null,
            loaded: false,
            showRedModal: false,
            showReasonPicker: false,
            redReason: '',
            redReasonText: '',
            redRemark: '',
            showEmailModal: false,
            emailAddr: '',
            refreshTimer: null,
            reasonColumns: [
                { text: '信息填写错误', value: '01' },
                { text: '重复开票', value: '02' },
                { text: '订单取消', value: '03' },
                { text: '其他原因', value: '04' }
            ]
        }
    },
    computed: {
        isFromHistory() {
            return this.$route.query.from === 'history';
        },
        statusText() {
            if (!this.invoice) return '';
            var fpzt = this.invoice.fpzt;
            if (fpzt === '1') return '已红冲';
            if (fpzt === '2') return '已作废';
            if (fpzt === '10') return '红冲申请中';
            if (fpzt === '20') return '部分红冲';
            var map = { '0': '待处理', '1': '开票中', '2': '已开票', '3': '开票失败' };
            return map[this.invoice.orderStatus] || '未知';
        },
        statusClass() {
            if (!this.invoice) return '';
            var fpzt = this.invoice.fpzt;
            if (fpzt === '1' || fpzt === '2') return 'red-cancelled';
            if (fpzt === '10' || fpzt === '20') return 'processing';
            var map = { '0': 'pending', '1': 'processing', '2': 'done', '3': 'failed' };
            return map[this.invoice.orderStatus] || '';
        },
        fpzlText() {
            if (!this.invoice || !this.invoice.fpzl) return '电子普通发票';
            var map = { '026': '增值税普通发票', '028': '增值税专用发票', '004': '增值税普通发票（卷式）', '10': '增值税电子普通发票', '14': '增值税电子专用发票' };
            return map[this.invoice.fpzl] || '电子普通发票';
        },
        isRedInvoice() {
            if (!this.invoice || !this.invoice.xsdjbh) return false;
            return this.invoice.xsdjbh.indexOf('HC') === 0;
        }
    },
    mounted() {
        this.loadData();
    },
    beforeDestroy() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    },
    methods: {
        loadData() {
            var xsdjbh = this.$route.query.xsdjbh;
            if (!xsdjbh) {
                this.loaded = true;
                return;
            }
            this.$axios.get('/api/v1/wygl/propertyPayment/invoiceStatus', {
                params: { xsdjbh: xsdjbh }
            }).then(res => {
                if (res) {
                    this.invoice = res;
                }
                this.loaded = true;
                this.startAutoRefresh();
            }).catch(() => {
                this.loaded = true;
            });
        },
        startAutoRefresh() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
                this.refreshTimer = null;
            }
            var needRefresh = this.invoice && (
                this.invoice.orderStatus === '1' ||
                (this.invoice.orderStatus === '2' && this.invoice.fpzt === '10')
            );
            if (!needRefresh) return;
            var that = this;
            this.refreshTimer = setInterval(function() {
                var xsdjbh = that.$route.query.xsdjbh;
                if (!xsdjbh) return;
                that.$axios.get('/api/v1/wygl/propertyPayment/invoiceStatus', {
                    params: { xsdjbh: xsdjbh }
                }).then(function(res) {
                    if (res) {
                        that.invoice = res;
                    }
                    // 状态已变为终态，停止刷新
                    var done = that.invoice && !(
                        that.invoice.orderStatus === '1' ||
                        (that.invoice.orderStatus === '2' && that.invoice.fpzt === '10')
                    );
                    if (done) {
                        clearInterval(that.refreshTimer);
                        that.refreshTimer = null;
                    }
                });
            }, 10000);
        },
        formatAmount(val) {
            if (!val && val !== 0) return '0.00';
            return Number(val).toFixed(2);
        },
        sendEmail() {
            if (!this.invoice) return;
            this.emailAddr = this.invoice.gfyx || '';
            this.showEmailModal = true;
        },
        confirmSendEmail() {
            if (!this.emailAddr) {
                this.$toast('请输入邮箱地址');
                return;
            }
            this.$axios.post('/api/v1/wygl/propertyPayment/sendInvoiceEmail', {
                xsdjbh: this.invoice.xsdjbh,
                email: this.emailAddr
            }).then(function() {
                this.$toast('发票已发送至邮箱');
                this.showEmailModal = false;
            }.bind(this)).catch(function() {
                this.$toast('发送失败，请重试');
            }.bind(this));
        },
        onReasonConfirm(val) {
            this.redReason = val.value;
            this.redReasonText = val.text;
            this.showReasonPicker = false;
        },
        confirmRed() {
            if (!this.redReason) {
                this.$toast('请选择红冲原因');
                return;
            }
            this.$axios.post('/api/v1/wygl/propertyPayment/redInvoice', {
                xsdjbh: this.invoice.xsdjbh,
                chyy: '03',
                remark: this.redRemark,
                zdkpbj: 'Y'
            }).then(function(res) {
                if (res && res.returnCode === '1000') {
                    this.showRedModal = false;
                    this.redReason = '';
                    this.redReasonText = '';
                    this.redRemark = '';
                    this.$toast.success('红冲申请已提交，正在处理中！');
                    setTimeout(function() {
                        this.$router.go(-1);
                    }.bind(this), 1500);
                } else {
                    this.$toast((res && res.returnMsg) || '红冲申请提交失败');
                }
            }.bind(this)).catch(function() {
                this.$toast('红冲申请提交失败');
            }.bind(this));
        },
        reInvoice() {
            this.$router.push({
                path: '/wyfw/invoicefill',
                query: {
                    id: this.invoice.def01,
                    typeName: this.invoice.typeName || '',
                    amount: Math.abs(this.invoice.jshj),
                    villageId: this.invoice.villageId || '',
                    type: this.invoice.def02 || '',
                    villageName: this.invoice.villageName || '',
                    ownerName: this.invoice.ownerName || '',
                    time: this.invoice.serviceTime || ''
                }
            });
        },
        downloadInvoice() {
            if (!this.invoice || !this.invoice.xsdjbh) {
                this.$toast('发票信息不完整');
                return;
            }
            var xsdjbh = this.invoice.xsdjbh;
            window.open('/api/v1/wygl/propertyPayment/viewInvoice?xsdjbh=' + xsdjbh + '&filetype=pdf', '_blank');
        }
    }
}
</script>

<style lang="less" scoped>
    .xtxx{
        height: calc(100% - 50px);
        width: 100%;
        display: flex;
        background-color: #f5f5f5;
        flex-direction: column;
        align-items: center;
        overflow-y: auto;
    }
    .content{
        height: 100%;
        width: 100%;
        padding: 15px;
        padding-bottom: 100px;
    }
    .invoice-card{
        background-color: #fff;
        border-radius: 10px;
        padding: 20px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.06);

        .invoice-type-header{
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 20px;
            .type-icon{
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background-color: #fff5f0;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 10px;
            }
            .type-text{
                font-size: 18px;
                font-weight: 600;
                color: #333;
            }
        }

        .status-tag{
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 500;
            flex-shrink: 0;
            &.pending{
                background-color: #fff3e0;
                color: #ff9800;
            }
            &.processing{
                background-color: #e3f2fd;
                color: #2196f3;
            }
            &.done{
                background-color: #e8f5e9;
                color: #4caf50;
            }
            &.failed{
                background-color: #fce4ec;
                color: #f44336;
            }
            &.red-cancelled{
                background-color: #fce4ec;
                color: #ef4444;
            }
        }

        .preview-divider{
            height: 1px;
            background-color: #f0f0f0;
            margin: 15px 0;
        }

        .info-section{
            .info-row{
                display: flex;
                justify-content: space-between;
                padding: 10px 0;
                border-bottom: 1px solid #f5f5f5;
                .info-label{
                    font-size: 14px;
                    color: #999;
                    flex-shrink: 0;
                }
                .info-value{
                    font-size: 14px;
                    color: #333;
                    text-align: right;
                    flex: 1;
                    word-break: break-all;
                    margin-left: 10px;
                }
            }
            .info-row:last-child{
                border-bottom: none;
            }
            .item-header-row{
                display: flex;
                justify-content: space-between;
                padding: 10px 0;
                border-bottom: 1px solid #f5f5f5;
                font-size: 13px;
                color: #999;
            }
            .item-row{
                display: flex;
                justify-content: space-between;
                padding: 12px 0;
                border-bottom: 1px solid #f5f5f5;
                .item-name{
                    font-size: 14px;
                    color: #333;
                    flex: 1;
                }
                .item-amount{
                    font-size: 14px;
                    color: #333;
                    font-weight: 500;
                    flex-shrink: 0;
                    margin-left: 10px;
                }
            }
        }

        .total-section{
            display: flex;
            justify-content: space-between;
            padding: 15px 0;
            border-top: 1px solid #f5f5f5;
            .total-label{
                font-size: 15px;
                color: #333;
                font-weight: 500;
            }
            .total-amount{
                font-size: 18px;
                color: #ff6b35;
                font-weight: 600;
            }
        }

        .remark-row{
            display: flex;
            padding: 10px 0;
            border-top: 1px solid #f5f5f5;
            .remark-label{
                font-size: 14px;
                color: #999;
                margin-right: 10px;
                flex-shrink: 0;
            }
            .remark-value{
                font-size: 14px;
                color: #333;
                flex: 1;
            }
        }

        .status-desc{
            margin-top: 15px;
            padding: 12px;
            background-color: #f5f5f5;
            border-radius: 8px;
            font-size: 13px;
            color: #666;
            text-align: center;
            &.failed{
                background-color: #fff3e0;
                color: #ff9800;
            }
        }

        .action-section{
            margin-top: 20px;
            display: flex;
            gap: 10px;
            .action-btn{
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 44px;
                line-height: 44px;
                text-align: center;
                border: none;
                border-radius: 8px;
                font-size: 15px;
                font-weight: 500;
                text-decoration: none;
                cursor: pointer;
                appearance: none;
                -webkit-appearance: none;
                padding: 0;
                &.secondary{
                    background-color: #ffffff;
                    color: #333;
                    border: 1px solid #ddd;
                }
                &.primary{
                    background: linear-gradient(135deg, #ff6b35, #ff8c42);
                    color: #fff;
                }
                &.red{
                    background: linear-gradient(135deg, #ef4444, #f87171);
                    color: #fff;
                }
            }
        }
    }

    /* 红冲弹窗 */
    .red-modal{
        .red-header{
            padding: 16px 20px;
            background: linear-gradient(135deg, #ef4444, #f87171);
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-radius: 16px 16px 0 0;
            .red-title{
                font-size: 17px;
                font-weight: 600;
                color: #fff;
                margin: 0;
            }
            .red-close{
                color: #fff;
                font-size: 20px;
                cursor: pointer;
            }
        }
        .red-body{
            padding: 20px;
            .red-form-item{
                margin-bottom: 16px;
                .red-form-label{
                    font-size: 14px;
                    color: #666;
                    margin-bottom: 8px;
                    display: block;
                    font-weight: 500;
                }
                .red-form-field{
                    padding: 0 12px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    background-color: #fff;
                }
            }
            .red-warning{
                display: flex;
                align-items: center;
                padding: 12px;
                background-color: #fffbeb;
                border-radius: 8px;
                margin-top: 8px;
                .van-icon{
                    font-size: 16px;
                    color: #92400e;
                    margin-right: 8px;
                }
                span{
                    font-size: 13px;
                    color: #92400e;
                }
            }
        }
        .red-footer{
            padding: 16px 20px;
            display: flex;
            gap: 12px;
            border-top: 1px solid #f0f0f0;
            .red-btn{
                flex: 1;
                height: 44px;
                border: none;
                border-radius: 10px;
                font-size: 16px;
                font-weight: 500;
                cursor: pointer;
                &.cancel{
                    background-color: #f5f5f5;
                    color: #666;
                }
                &.confirm{
                    background: linear-gradient(135deg, #ef4444, #f87171);
                    color: #fff;
                }
            }
        }
    }

    .empty-state, .loading-state{
        text-align: center;
        padding: 60px 0;
        color: #999;
        font-size: 14px;
    }
    ::-webkit-scrollbar{
        display: none;
    }
</style>
